/**
*
* NavBar
*
*/

import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { generateTempFieldId } from 'utils/idUtils';
import { ALL_BRANCHES } from 'utils/conditionalConstants';
import { DATE_OPTIONS } from 'utils/constants';
import { withRouter } from 'react-router-dom';
import { handleSaveFormCreate } from 'containers/FormBuilder/hooks/handleSaveFormCreate';
import BreadCrumb from 'components/BreadCrumb';
import SelectAttribute from 'components/SelectAttribute';
import FormMetadataModal from 'components/FormMetadataModal';
import NavBarExtras from './NavBarExtras';
import { handleSaveFormUpdate, handleCloneForm, handlePublishForm, handleUnpublishForm } from './utils';

const NavBar = (props) => {
  const dispatch = useDispatch();

  const onAdd = (action) => {
    const id = generateTempFieldId();
    action(id);
    setTimeout(() => {
      props.scrollToBottom();
    }, 10);
  };

  const onPublishForm = () => {
    const { id } = props.form;
    const date = new Date().toISOString();
    handlePublishForm(id, date, dispatch);
  };

  const onUnpublishForm = () => {
    const { id } = props.form;
    handleUnpublishForm(id, dispatch);
  };

  const onCloneForm = (options) => {
    const { id, groupingId } = props.form;
    handleCloneForm(id, groupingId, options, dispatch);
  };

  const onCollapseAllFields = () => {
    props.snapToTop();
    props.collapseAllFields();
  };

  const formValid = () => {
    if (props.invalidFormData && Object.keys(props.invalidFormData).length) {
      return false;
    }
    return true;
  };

  const shouldSuppressPublish = () => props.isFormDirty || !props.form.id;

  const saveForm = async () => {
    if (props.form.id) {
      return handleSaveFormUpdate(props.form, dispatch);
    }
    return handleSaveFormCreate(props.form, dispatch);
  };

  const showAndScrollToErrors = () => {
    props.showFormErrors();
    props.scrollToTop();
  };

  const formatDate = (publishedDateFormat) => publishedDateFormat.toLocaleString('en-US', DATE_OPTIONS)
    .split(',')
    .reduce((accumulator, present, index) => {
      if ((index) === 1) {
        return accumulator.concat(present).concat(',');
      }

      if ((index) === 2) {
        return accumulator.concat(present).concat(' •');
      }

      return accumulator.concat(present);
    }, '');

  const { activeBranches, form: { id = '', groupingId, name, lastPublished, isPublished }, visibleBranches, match } = props;
  const { credentialTypeId } = match.params;
  let preview = (
    <a
      className="btn btn-sm btn-outline-secondary m-2"
      id="preview-form-btn"
      href={`/credential-type/${credentialTypeId}/groupings/${groupingId}/forms/${id}/preview/`}
      rel="noopener noreferrer"
      role="button"
      target="_blank"
    >
      Preview
    </a>
  );
  let lastPublishedDisplay = null;

  if (props.isFormDirty) {
    preview = (<button type="button" className="btn btn-sm btn-outline-secondary m-2" id="preview-form-btn" disabled>Preview</button>);
  }

  if (isPublished && lastPublished) {
    lastPublishedDisplay = (
      <span id="lastPublished" className="ml-2" style={{ fontSize: '13px', color: '#666633' }}>
        Last published {formatDate(new Date(lastPublished))}
      </span>
    );
  }

  let branchSelector = null;
  if (Object.keys(activeBranches).length) {
    const branchingFieldId = Object.keys(activeBranches)[0];
    const options = activeBranches[branchingFieldId].reduce((acc, curr) => {
      acc[curr.value] = `Branch ${curr.index + 1}`;
      return acc;
    }, { [ALL_BRANCHES]: 'All Branches' });
    branchSelector = (
      <div className="form-group m-0">
        <SelectAttribute
          className="form-control-sm"
          disableNullOption
          id="select-visible-branches-dropdown"
          keyPath={['visibleBranches']}
          onFieldValueChange={props.updateUIState}
          options={options}
          value={visibleBranches}
        />
      </div>
    );
  }

  return (
    <nav className="bg-white border border-secondary border-top-0 border-left-0 border-right-0 py-3">
      <div className="container p-0">
        <div className="row">
          <div className="col-5">
            <BreadCrumb
              path={
                props.path
              }
            />
            <h1 className={`h4 m-0 mb-2${name.length ? '' : ' text-muted'}`}>
              {name.length ? name : 'Untitled Form'}
            </h1>
          </div>
          <div className="col-7 float-right">
            <span className="float-right">
              <button className="btn btn-sm btn-outline-secondary m-2" id="new-field-btn-top" type="button" onClick={() => onAdd(props.onAddField)}>+ Add Field</button>
              <button className="btn btn-sm btn-outline-secondary m-2" id="new-step-btn-top" type="button" onClick={() => onAdd(props.onAddStep)}>+ Add Step</button>
              {preview}
              <NavBarExtras
                isPublished={props.form.isPublished || false}
                onCloneForm={onCloneForm}
                onPublishForm={onPublishForm}
                shouldSuppressPublish={shouldSuppressPublish()}
                onUnpublishForm={onUnpublishForm}
              />
              <button
                className="btn btn-sm btn-primary my-2 ml-2"
                id="save-form-btn"
                type="button"
                onClick={formValid() ? saveForm : showAndScrollToErrors}
                disabled={props.loadInProgress}
              >Save Form
              </button>
            </span>
          </div>
          {
            props.form.isExperimentalForm && (
              <div
                className="col-12"
                style={{ marginBottom: '0.5rem' }}
              >
                <div className="navbar navbar-warning bg-warning rounded">
                  <h5 className="col-5" style={{ margin: '0px', paddingLeft: '0px' }}>
                    Experimental Form
                  </h5>
                  <div className="col-7 float-right">
                    <span className="float-right">
                      <FormMetadataModal
                        isExperimentalForm={props.form.isExperimentalForm}
                        formMetadata={props.form.formMetadata}
                      />
                    </span>
                  </div>
                </div>
              </div>
            )
          }

        </div>
        <div className="row">
          <div className={`col${id.length ? '' : ' invisible'}`}>
            FormID: {id}
            {lastPublishedDisplay}
          </div>
          <div className="col d-flex justify-content-end align-items-center text-primary small">
            <button onClick={props.scrollToEmbedForm} type="button" id="embed-config-btn">Embed config</button>
            <span className="text-secondary">|</span>
            <button onClick={props.expandAllFields} type="button" id="expand-all-fields-btn">Expand All</button>
            <span className="text-secondary">|</span>
            <button onClick={onCollapseAllFields} type="button" id="collapse-all-fields-btn">Collapse All</button>
            {branchSelector}
          </div>
        </div>
      </div>
    </nav>
  );
};

NavBar.propTypes = {
  activeBranches: PropTypes.object.isRequired,
  collapseAllFields: PropTypes.func.isRequired,
  expandAllFields: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  invalidFormData: PropTypes.object,
  isFormDirty: PropTypes.bool.isRequired,
  loadInProgress: PropTypes.bool.isRequired,
  onAddField: PropTypes.func.isRequired,
  onAddStep: PropTypes.func.isRequired,
  showFormErrors: PropTypes.func.isRequired,
  scrollToBottom: PropTypes.func.isRequired,
  scrollToEmbedForm: PropTypes.func.isRequired,
  scrollToTop: PropTypes.func.isRequired,
  snapToTop: PropTypes.func.isRequired,
  visibleBranches: PropTypes.string.isRequired,
  updateUIState: PropTypes.func.isRequired,
  path: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    link: PropTypes.string,
  })).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      credentialTypeId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default withRouter(NavBar);
